import './index.css';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { ready } from 'libsodium-wrappers';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Storro from './api';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_MOBILE_APP } from './const';
import { BannerProvider } from './context/BannerContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { ClientPortalProvider } from './context/ClientPortalContext';
import { DialogProvider } from './context/DialogContext';
import { MenuProvider } from './context/MenuContext';
import { PageLoaderProvider } from './context/PageLoaderContext';
import { ProjectProvider } from './context/ProjectContext';
import { RealmProvider } from './context/RealmContext';
import { SidebarLeftProvider } from './context/SidebarLeftContext';
import { SidebarRightProvider } from './context/SidebarRightContext';
import { SkipPlanProvider } from './context/SkipSelectPlanContext';
import { SplitScreenProvider } from './context/SplitScreenContext';
import { StorroApiProvider } from './context/StorroApiContext';
import { UserProvider } from './context/UserContext';
import LoaderWrapper from './LoaderWrapper';
import { envVar, getEnvUrls, validEnvVars } from './util/Util/EnvVar';
import { PageActionProvider } from 'context/PageActionContext';
import { DEFAULT_THEME, ThemeProvider } from 'context/ThemeContext';
import { HelmetProvider } from 'react-helmet-async';
import { ProjectContributorsProvider } from 'context/ProjectContributorsContext';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import backend from 'i18next-http-backend';
import { logger } from 'util/Logger';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FileViewerProvider } from 'context/FileViewerContext';
import { createError } from 'util/FormatError';

// We need lib sodium right from the start. Wait for ready before doing anything else.
await ready;

/**
 * Init the Api and pass it to the StorroApiProvider
 * This way we ensure that the API is loaded one time and no more than that
 */
const initApi = (): Storro => {
  const { apiUrl, wsUrl } = getEnvUrls();
  return new Storro(apiUrl, wsUrl);
};

const container = document.getElementById('root');
const root = createRoot(container!);

// eslint-disable-next-line
// @ts-ignore
const currentGitCommitHash = `${GIT_COMMIT_HASH}`;
// eslint-disable-next-line
// @ts-ignore
const currentGitVersionTag = `${GIT_VERSION_TAG}`;

// init Bugsink for error tracking, but only in staging and production
if (envVar('APP_ENV', false) !== 'development') {
  Sentry.init({
    dsn: envVar('BUGSINK_DSN', false),
    release: currentGitCommitHash,
    initialScope: {
      tags: {
        commit: currentGitCommitHash,
        version: currentGitVersionTag,
      },
    },
    integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
    // not supported by Bugsink
    // @see https://www.bugsink.com/docs/sdk-recommendations/
    tracesSampleRate: 0,
    // Normalize dynamic import errors to a single fingerprint, regardless of how the browser
    // reports them (unhandledrejection, console.error, etc.). This avoids unique error messages
    // per deployment URL and groups them into one issue in Bugsink.
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
      const isModuleLoadError =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed');

      if (isModuleLoadError) {
        event.fingerprint = ['failed-to-fetch-dynamically-imported-module'];
        // Store the original message (including the chunk URL) as a tag for debugging
        event.tags = { ...event.tags, origMessage: message };
        // Normalize the message to remove the deployment-specific chunk URL
        if (event.exception?.values?.[0]) {
          event.exception.values[0].value = 'Failed to fetch dynamically imported module';
        }
        event.message = 'Failed to fetch dynamically imported module';
      }

      return event;
    },
  });
}

// Init translations
i18next
  .use(initReactI18next)
  .use(backend)
  .use(LanguageDetector)
  .init(
    {
      load: 'languageOnly', // fix 404 for en-US etc. --> https://github.com/i18next/i18next-http-backend?tab=readme-ov-file#seeing-failed-http-requests-like-404
      // debug: true, // Useful for debugging, displays which key is missing
      fallbackLng: 'en', // If translation key is missing, which lang use instead
      returnEmptyString: false, // Empty strings in translation will go to the fallbackLng
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
      detection: {
        lookupQuerystring: 'lng', // Get language from query param
        order: ['querystring', 'navigator'], // Only detect the language from the browser navigator.
        caches: [], // Don't store the language in i.e. local-storage
      },
      backend: {
        queryStringParams: { v: currentGitCommitHash },
      },
    },
    err => {
      if (err) {
        logger.error(createError('Failed to load i18next', err));
      }
    },
  );

// Create a client
const queryClient = new QueryClient();

/**
 * Only load the app when all Env Vars are valid
 */
if (validEnvVars()) {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <Sentry.ErrorBoundary fallback={({ eventId }) => <ErrorBoundary eventId={eventId} />}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={DEFAULT_THEME}>
              <HelmetProvider>
                <SkipPlanProvider skipPlan={false}>
                  <RealmProvider initialLoaded={false}>
                    <UserProvider>
                      <StorroApiProvider storroApi={initApi()}>
                        <PageLoaderProvider loading={false}>
                          <BreadcrumbProvider>
                            <PageActionProvider>
                              <MenuProvider>
                                <BannerProvider>
                                  <LoaderWrapper>
                                    <SidebarRightProvider>
                                      <SidebarLeftProvider>
                                        <DialogProvider dialogsItems={[]}>
                                          <SplitScreenProvider showSidebar={false}>
                                            <ClientPortalProvider>
                                              <ProjectProvider>
                                                <ProjectContributorsProvider>
                                                  <FileViewerProvider>
                                                    <App />
                                                  </FileViewerProvider>
                                                </ProjectContributorsProvider>
                                              </ProjectProvider>
                                            </ClientPortalProvider>
                                          </SplitScreenProvider>
                                        </DialogProvider>
                                      </SidebarLeftProvider>
                                    </SidebarRightProvider>
                                  </LoaderWrapper>
                                </BannerProvider>
                                <ToastContainer stacked={true} position={IS_MOBILE_APP ? 'bottom-right' : 'top-right'} />
                              </MenuProvider>
                            </PageActionProvider>
                          </BreadcrumbProvider>
                        </PageLoaderProvider>
                      </StorroApiProvider>
                    </UserProvider>
                  </RealmProvider>
                </SkipPlanProvider>
              </HelmetProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      </BrowserRouter>
    </React.StrictMode>,
  );
} else {
  root.render(
    <React.StrictMode>
      <p>{'Invalid environment variables. See console log for more info.'}</p>
    </React.StrictMode>,
  );
}
